import { Helmet } from 'react-helmet-async';

const DEFAULT_TITLE = "Unight | UK & India's Underground Nightlife Platform";
const DEFAULT_DESCRIPTION =
  "Discover underground events, connect with the nightlife community, and never miss out on unforgettable nights across the UK and India.";
const DEFAULT_IMAGE = 'https://unighttickets.com/logo.png';
const CANONICAL_ORIGIN = 'https://unighttickets.com';

/**
 * Per-route SEO tags. Renders a <Helmet> with unique <title>,
 * <meta description>, canonical, and full OG / Twitter card pack so each
 * indexable page has its own signals instead of the default site-wide ones.
 *
 * Passing `noindex` on private surfaces (dashboards, /profile, /my-tickets)
 * mirrors the robots.txt disallow so search engines drop them on re-crawl.
 *
 * Props:
 *   title       — page-specific title (auto-suffixed with " · Unight")
 *   description — 1-2 sentence page summary, ~150-160 chars ideal
 *   path        — pathname to build canonical URL (defaults to "/")
 *   image       — social preview image (defaults to /logo.png)
 *   noindex     — set for auth-required / private pages
 */
export default function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '/',
  image = DEFAULT_IMAGE,
  noindex = false,
}) {
  const fullTitle = title ? `${title} · Unight` : DEFAULT_TITLE;
  const canonical = `${CANONICAL_ORIGIN}${path.startsWith('/') ? path : '/' + path}`;
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Unight" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
}
